import { useMount } from 'ahooks';
import { Checkbox, Col, Form, Radio, Row } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { OrientationEnum, SkuSize } from 'domains/skuDomain';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SKU_TYPE_NAME_PACKAGING } from 'utils/constants';
import { StepComponentProps } from '.';
import { BothSize } from '../components/BothSize';
import { PackageSize } from '../components/PackageSize';
import { SignleSize } from '../components/SignleSize';
import './Step2Size.less';

export const Step2Size = ({ sku, topSku, skuPart }: StepComponentProps) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [draftSize, setDraftSize] = useState<SkuSize[] | undefined>(
    sku?.skuSizes || []
  );
  const [orientation, setOrientation] = useState<OrientationEnum[] | undefined>(
    sku?.orientation || []
  );
  const [skipSizeAndOrientation, setSkipSizeAndOrientation] = useState<boolean>(
    sku?.skipSizeAndOrientation || false
  );
  const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
  };
  useMount(() => {
    setOrientation(sku?.orientation);
    setDraftSize(sku?.skuSizes);
    form.setFieldsValue({
      orientation: sku?.orientation,
      skipSizeAndOrientation: Boolean(sku?.skipSizeAndOrientation),
    });
  });

  const isPackagingSku = useMemo(() => {
    return (
      topSku?.skuType?.name === SKU_TYPE_NAME_PACKAGING ||
      sku?.skuType?.name === SKU_TYPE_NAME_PACKAGING
    );
  }, [topSku?.skuType?.name, sku?.skuType?.name]);

  const fieldsChange = (changedFields: any[], allFields: any[]) => {
    setOrientation(
      allFields.filter((item) => item.name.includes('orientation'))[0].value
    );
  };

  return (
    <>
      <div className="single-sku-config-wizard-step-title">
        <span className="brand-green">
          {t('platform.sku.wizard.whichSizes')}
        </span>{' '}
        {t('platform.sku.wizard.willYouSupport')}
      </div>
      <div className="single-sku-config-wizard-step-content">
        <Form
          form={form}
          className="single-sku-config-sizes"
          {...formItemLayout}
          autoComplete="off"
          onFieldsChange={fieldsChange}
          onValuesChange={(value, allValue) => {
            setSkipSizeAndOrientation(allValue.skipSizeAndOrientation);
          }}
        >
          <Form.Item
            labelCol={{
              flex: 'none',
            }}
            wrapperCol={{
              flex: 'none',
            }}
            label={
              isPackagingSku
                ? t('platform.sku.wizard.quantity.form.label.sizesPackingSkip')
                : t(
                    'platform.sku.wizard.quantity.form.label.sizesOrientationSkip'
                  )
            }
            name="skipSizeAndOrientation"
          >
            <Radio.Group disabled={sku.readonly || Boolean(topSku)}>
              <Radio value={true}>
                <span>Yes</span>
              </Radio>
              <Radio value={false}>
                <span>No</span>
              </Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            hidden={isPackagingSku}
            name="orientation"
            label={t(
              'platform.sku.wizard.quantity.form.label.orientationSupport'
            )}
            rules={[{ type: 'array', required: true }]}
          >
            <Checkbox.Group disabled={sku.readonly || Boolean(topSku)}>
              <Row>
                <Col span={12}>
                  <Checkbox
                    value="portrait"
                    style={{ lineHeight: '32px' }}
                    disabled={
                      form?.getFieldValue('orientation')?.length === 1 &&
                      form?.getFieldValue('orientation')?.includes('portrait')
                    }
                  >
                    {t('platform.sku.wizard.quantity.form.label.portrait')}
                  </Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox
                    value="landscape"
                    style={{ lineHeight: '32px' }}
                    disabled={
                      form?.getFieldValue('orientation')?.length === 1 &&
                      form?.getFieldValue('orientation')?.includes('landscape')
                    }
                  >
                    {t('platform.sku.wizard.quantity.form.label.landscape')}
                  </Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form>

        {isPackagingSku ? (
          <PackageSize
            sku={{ ...sku, orientation: orientation, skuSizes: draftSize }}
            skipSizeAndOrientation={skipSizeAndOrientation}
            topSku={topSku}
            skuPart={skuPart}
            setDraftSize={setDraftSize}
          />
        ) : (
          <>
            {orientation?.length === 2 ? (
              <BothSize
                sku={{ ...sku, orientation: orientation, skuSizes: draftSize }}
                skipSizeAndOrientation={skipSizeAndOrientation}
                topSku={topSku}
                skuPart={skuPart}
                setDraftSize={setDraftSize}
              />
            ) : (
              <SignleSize
                sku={{ ...sku, orientation: orientation, skuSizes: draftSize }}
                skipSizeAndOrientation={skipSizeAndOrientation}
                topSku={topSku}
                skuPart={skuPart}
                setDraftSize={setDraftSize}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};
